import React from "react";
import { Link, Navigate } from "react-router-dom";
import MailingListSignup from "../components/MailingListSignup";
import { useAuth } from "../context/AuthContext";
import { pickWeek } from "../games/pickThree";
import { startGuidedDemo } from "../tours/guidedDemo";
import wordmarkOnDark from "../assets/logo-wordmark-on-dark.png";
import stadium from "../assets/games/pick-three.jpg";
import appIcon from "../assets/app-icon-small.png";

// The signed-out front page. Lineup, the season game, is the product, and this
// page is ordered to say so:
//
//   1. A photo header with the wordmark and the two account buttons.
//   1b. What makes it different, said outright: "Draft teams, not players.",
//      and the loop in three short steps. Text only; no school logos on a
//      public front page, where they would decorate rather than identify.
//   2. The guided tour of the demo league, the best way to see the game
//      before signing up, as the one strong action below the header. An
//      outlined button, not a card: a card with a status dot read as a
//      template when that was tried.
//   3. "How does it work?" as a plain link.
//   4. Pick Three, the free weekly game, as a small row near the bottom. It is
//      a way in for people who want something lighter, not the product, so it
//      must not compete with the above. Shown only while a lineup week is
//      running (weeks 1 to 13); the season config is readable signed out and
//      AuthContext loads it.
//   5. The "next season" email box (double opt-in, migration 081), only
//      outside the season. In season it would ask people to wait for something
//      already being played, and the demo and Pick Three are better ways to
//      keep a visitor.
//
// The header image is the stadium photo Pick Three uses (assets/games, with
// its credit note) under the same navy scrim, with the white-ink wordmark,
// which is legible on it in either theme. The Pick Three row carries the app
// icon instead of a second copy of that photo: app-icon-small.png is
// assets/icon.png (the App Store icon) with its dark frame cropped, at 132px.

const NAVY = "#071F39";

// The loop, one line each. Five starting slots (STARTER_SLOTS), but an empty
// slot is a legal hedge, hence "up to five".
const STEPS = [
  { title: "Draft college teams", detail: "With your league, not individual players." },
  { title: "Start up to five each week", detail: "And make one your captain to double its points." },
  { title: "Score on the result", detail: "Wins and beating the spread earn points." },
];

const footerLink = { color: "var(--color-text-faint)", fontSize: 13, textDecoration: "none" };

function Landing() {
  const { currentUser, seasonConfig } = useAuth();
  const inSeason = pickWeek(seasonConfig?.currentWeek) !== null;

  if (currentUser) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="theme-auto" style={{ minHeight: "100vh", backgroundColor: "var(--color-bg-card)", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "max(64px, calc(env(safe-area-inset-top) + 40px)) 24px 28px",
          background: `linear-gradient(180deg, rgba(7,31,57,0.45) 0%, rgba(7,31,57,0.94) 100%), url(${stadium}) center / cover`,
          color: "#fff",
        }}
      >
        <div style={{ maxWidth: 360, margin: "0 auto" }}>
          <img src={wordmarkOnDark} alt="Lineup CFB" style={{ width: 220, maxWidth: "70%", height: "auto", display: "block", margin: "0 auto" }} />
          <p style={{ fontSize: 16, textAlign: "center", color: "rgba(255,255,255,0.9)", margin: "16px 0 28px" }}>
            College football fantasy, reimagined.
          </p>
          <Link
            to="/signup"
            style={{ display: "block", backgroundColor: "#fff", color: NAVY, textAlign: "center", padding: "16px 0", borderRadius: 50, fontWeight: 700, fontSize: 16, textDecoration: "none" }}
          >
            Get started
          </Link>
          <Link
            to="/login"
            style={{ display: "block", marginTop: 12, color: "#fff", textAlign: "center", padding: "14px 0", borderRadius: 50, fontWeight: 600, fontSize: 16, textDecoration: "none", border: "2px solid rgba(255,255,255,0.8)" }}
          >
            Log in
          </Link>
        </div>
      </header>

      <main style={{ width: "100%", maxWidth: 360, margin: "0 auto", padding: "24px 24px 0", boxSizing: "border-box" }}>
        <section aria-labelledby="usp" style={{ marginBottom: 24 }}>
          <h2 id="usp" style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.01em", textAlign: "center", color: "var(--color-text-strong)", margin: "0 0 16px" }}>
            Draft teams, not players.
          </h2>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
            {STEPS.map((step, i) => (
              <li key={step.title} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: "var(--color-cta)", color: "var(--color-cta-text)", fontSize: 13, fontWeight: 800,
                  }}
                >
                  {i + 1}
                </span>
                <span>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "var(--color-text-strong)" }}>{step.title}</span>
                  <span style={{ display: "block", fontSize: 13, color: "var(--color-text-muted)" }}>{step.detail}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>

        <Link
          to="/demo"
          onClick={startGuidedDemo}
          style={{ display: "block", color: "var(--color-cta)", textAlign: "center", padding: "14px 0", borderRadius: 50, fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid var(--color-cta)" }}
        >
          Take the tour of a demo league
        </Link>
        <Link
          to="/how-to-play"
          style={{ display: "block", color: "var(--color-cta)", textAlign: "center", padding: "12px 0", marginTop: 6, fontSize: 15, fontWeight: 600, textDecoration: "none" }}
        >
          How does it work? →
        </Link>

        {!inSeason && <MailingListSignup style={{ marginTop: 24 }} />}

        {inSeason && (
          <Link
            to="/pick-three"
            style={{
              display: "flex", alignItems: "center", gap: 12, marginTop: 36, padding: "10px 12px", borderRadius: 14,
              border: "1px solid var(--color-border)", textDecoration: "none", color: "var(--color-text-strong)",
            }}
          >
            <img src={appIcon} alt="" aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "block" }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>Play Pick Three, free</span>
              <span style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginTop: 1 }}>
                A quick weekly game. No league needed.
              </span>
            </span>
            <span aria-hidden="true" style={{ fontSize: 18, color: "var(--color-text-muted)" }}>→</span>
          </Link>
        )}
      </main>

      <footer style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", padding: "32px 24px", marginTop: "auto" }}>
        <Link to="/privacy" style={footerLink}>Privacy Policy</Link>
        <Link to="/terms" style={footerLink}>Terms of Service</Link>
        <Link to="/support" style={footerLink}>Support</Link>
        <Link to="/delete-account" style={footerLink}>Delete Account</Link>
      </footer>
    </div>
  );
}

export default Landing;
